import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import { useMenu } from '../../hooks/useMenu';
import { retrieveFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import {
  ACCOUNT_TYPE,
  ACCOUNT_TYPE_GTM,
  APPLICATION_TYPE,
} from '../../constants/sessionStorage';
import { APPLICATION_TYPE_STRING, IRAAccountType } from '../../util/constants';
import useApplicationOwnerCountry from '../../hooks/useApplicationOwnerCountry';
import useApplicationJointOwnersCountries from '../../hooks/useApplicationJointOwnersCountries';
import useApplicationJointBackupWithholdings from '../../hooks/useApplicationJointBackupWitholding';
import useApplicationMinorOwnersCountries from '../../hooks/useApplicationMinorOwnerCountries';
import OnBoardingCta from '../../components/OnBoarding/OnBoardingCta';
import OnBoardingContainer from '../../components/OnBoarding/OnBoardingContainer';
import { HeadingSubtitle } from '../../components/Heading';
import Paragraph from '../../components/Paragraph/Paragraph';
import AdditionalInfo from '../../components/AdditionalInfo';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import useStepPost from '../../hooks/useStepPost';
import themeColors from '../../assets/styles/themeColors';
import SaveAndCloseModal from '../../components/Modals/SaveAndCloseModal/SaveAndCloseModal';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';
import { ACCOUNT_STEP_VIEW } from '../../util/gtmEvents';
import { useApplicantData } from '../../hooks/useApplicantData';

const SecureAddress = ({ applicantType }) => {
  const { t } = useTranslation();
  const { activeMenu, activeItem } = useMenu();
  const { onSuccess } = useStepPost({ nextPath: activeItem?.nextPath });
  const AccountType = retrieveFromSessionStorage(ACCOUNT_TYPE);
  const ApplicantData = useApplicantData(applicantType);
  const iraPersonalInformation =
    AccountType === IRAAccountType[1] || AccountType === IRAAccountType[2];
  const { identityCountry } = useApplicationOwnerCountry(applicantType);
  const { secondaryIdentityCountry } = useApplicationJointOwnersCountries();
  const {
    mainBackupWithHolding,
    secondaryBackupWithHolding,
  } = useApplicationJointBackupWithholdings();
  const { minorIdentityCountry } = useApplicationMinorOwnersCountries();

  const isJointFlow =
    retrieveFromSessionStorage(APPLICATION_TYPE) === APPLICATION_TYPE_STRING[4];
  const isMinorFlow =
    retrieveFromSessionStorage(APPLICATION_TYPE) === APPLICATION_TYPE_STRING[5];

  const { BrokerCode, BrokerName } = useSelector(selectSettings);
  const [eventType, setEventType] = useState('NoForm');
  const [saveAndCloseModalOpen, setSaveAndCloseModalOpen] = useState(false);

  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);

  useEffect(() => {
    if (
      ApplicantData?.PersonName &&
      ApplicantData?.PersonBirthday &&
      ApplicantData?.Phone?.LocalNumber
    ) {
      gtmStepHelper({
        gtmStepEvent,
        eventName: ACCOUNT_STEP_VIEW,
        stepLabel: 'Address',
        accountType: accountGtmType,
        accountName: ApplicantData?.PersonName,
        accountDateOfBirth: ApplicantData?.PersonBirthday,
        accountPhone: ApplicantData?.Phone,
        customParams: {
          timestamp: new Date().toISOString(),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ApplicantData?.Phone?.LocalNumber]);

  const getPreviousPath = () => {
    if (isJointFlow) {
      if (mainBackupWithHolding || secondaryBackupWithHolding) {
        return activeItem?.previousPath;
      }

      if (identityCountry === 'USA' || secondaryIdentityCountry === 'USA') {
        return activeItem?.dependentSkipPath;
      }

      return activeItem?.preSkipPath;
    }

    if (isMinorFlow) {
      if (minorIdentityCountry === 'USA') {
        return activeItem?.previousPath;
      }

      return activeItem?.preSkipPath;
    }

    if (identityCountry !== 'USA') {
      return activeItem?.preSkipPath;
    }

    return activeItem?.previousPath;
  };

  return (
    <OnBoardingContainer
      activeMenu={activeMenu}
      activeItem={activeItem}
      previousPath={getPreviousPath()}
      setEventType={setEventType}
      eventType={eventType}
      setSaveAndCloseModalOpen={setSaveAndCloseModalOpen}
    >
      <Helmet>
        <title>
          {t('secureAddress.titleTag', {
            brokerName: BrokerName,
          })}
        </title>
      </Helmet>
      <HeadingSubtitle mb={12} color={themeColors.colorTextSecondary}>
        {t('secureAddress.title')}
      </HeadingSubtitle>
      <Paragraph color={themeColors.colorTextXLLight}>
        {t('secureAddress.subtitle')}
      </Paragraph>
      <OnBoardingCta
        title={t('continue')}
        onClick={() => {
          onSuccess();
        }}
      />

      <AdditionalInfo
        highlight={t('secureAddress.highlight')}
        info={t(
          iraPersonalInformation
            ? 'secureAddress.infoPersonalIra'
            : 'secureAddress.info',
          { brokerCode: BrokerCode },
        )}
      />
      {saveAndCloseModalOpen && (
        <SaveAndCloseModal
          isOpen={saveAndCloseModalOpen}
          onClose={() => setSaveAndCloseModalOpen(false)}
        />
      )}
    </OnBoardingContainer>
  );
};

SecureAddress.propTypes = {
  applicantType: PropTypes.string,
};

export default SecureAddress;
